import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { firstValueFrom, filter, take, timeout, catchError, of } from 'rxjs';
import { User } from '@angular/fire/auth';
import { PlatformService } from '@app/core/platform/platform.service';
import { isAdminUser } from '../admin/admin.config';
import { AuthService } from '../services/auth.service';
import { SnackbarService } from '../services/snackbar.service';
import { CollaboratorService } from '../services/collaborator.service';

/**
 * Route guard: only approved Execution Partners — or an admin — may access
 * the partner dashboard (/partners/dashboard/**).
 *
 * "Approved" resolution is delegated entirely to
 * CollaboratorService.resolvePartnerAccess(uid, email), which:
 *   1. Matches an existing Collaborator record by uid (the fast path for
 *      every login after the first).
 *   2. Falls back to a normalized email match and CLAIMS the record (writes
 *      uid onto it) the first time a match is found — so an admin-created
 *      collaborator (email only, no uid yet) becomes accessible the moment
 *      that person logs in with the matching email, with no separate
 *      "connect your account" step required.
 *
 * This guard only adds the authorization judgment on top of that lookup:
 * the resolved record must have status === 'active'. A paused/offboarded
 * collaborator has a doc (possibly even a claimed uid from before), but
 * loses dashboard access immediately since resolvePartnerAccess itself
 * makes no status decision — that's intentionally left to the caller here.
 *
 * SSR-safe: proceeds on server (the partner dashboard has no server-
 * renderable value; this is a client-only surface), re-checks in browser.
 */
export const partnerGuard: CanActivateFn = async () => {
  const auth = inject(AuthService);
  const platform = inject(PlatformService);
  const router = inject(Router);
  const collaborators = inject(CollaboratorService);
  const snack = inject(SnackbarService);

  if (platform.isServer) return true;

  const user = await firstValueFrom(
    auth.user$.pipe(
      filter((u): u is User | null => u !== undefined),
      take(1),
      timeout(4000),
      catchError(() => of(null)),
    ),
  );

  if (!user) {
    snack.showInfo('Please log in to view this page.');
    router.navigateByUrl('/login');
    return false;
  }

  if (isAdminUser(user.email, user.uid)) return true;

  try {
    const collaborator = await collaborators.resolvePartnerAccess(
      user.uid,
      user.email,
    );
    if (collaborator && collaborator.status === 'active') return true;
  } catch {
    // fall through to the denial path below
  }

  snack.showInfo("You don't have access to the partner dashboard.");
  router.navigateByUrl('/partners');
  return false;
};